import { useState, useEffect } from 'react';
import { eventRepository } from '../../../../domain/repositories';

/**
 * Custom hook to handle adding event to Google Calendar
 * @param {Object} eventData - Event data object
 * @returns {Function} Handler function to add event to calendar
 */
export const useAddToCalendar = (eventData) => {
  const handleAddToCalendar = () => {
    if (!eventData) return;

    // Parse date and time
    const [day, month, year] = eventData.date.split('/').map(Number);
    const [startHour, startMinute] = eventData.hourStart ? eventData.hourStart.split(':').map(Number) : [0, 0];
    
    // Create start date
    const startDate = new Date(year, month - 1, day, startHour, startMinute);
    
    // Create end date
    let endDate;
    if (eventData.dateEnd) {
      const [endDay, endMonth, endYear] = eventData.dateEnd.split('/').map(Number);
      const [endHour, endMinute] = eventData.hourEnd ? eventData.hourEnd.split(':').map(Number) : [23, 59];
      endDate = new Date(endYear, endMonth - 1, endDay, endHour, endMinute);
    } else {
      // If no end date, use start date + 2 hours or end time if provided
      const [endHour, endMinute] = eventData.hourEnd ? eventData.hourEnd.split(':').map(Number) : [startHour + 2, startMinute];
      endDate = new Date(year, month - 1, day, endHour, endMinute);
    }

    // Format dates for Google Calendar (YYYYMMDDTHHmmssZ)
    const formatGoogleDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
    };

    // Create description without HTML tags
    const stripHtml = (html) => {
      if (!html) return '';
      const tmp = document.createElement('div');
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || '';
    };

    // Build Google Calendar URL
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: eventData.title || 'Evento',
      dates: `${formatGoogleDate(startDate)}/${formatGoogleDate(endDate)}`,
      details: stripHtml(eventData.description) || '',
      location: eventData.place || '',
    });

    const googleCalendarUrl = `https://calendar.google.com/calendar/render?${params.toString()}`;
    window.open(googleCalendarUrl, '_blank');
  };

  return handleAddToCalendar;
};

/**
 * Custom hook to fetch event details with related events
 * @param {string|number} eventId - Event ID from URL params
 * @returns {Object} Object containing event, related events, loading state, and error
 */
export const useEventDetail = (eventId) => {
  const [data, setData] = useState({
    event: null,
    relatedEvents: []
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setError('Event ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const eventData = await eventRepository.getEventWithRelated(eventId, 3);
        setData(eventData);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError(err.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  return { ...data, loading, error };
};

/**
 * Custom hook to fetch only event details without related events
 * @param {string|number} eventId - Event ID
 * @returns {Object} Object containing event, loading state, and error
 */
export const useEvent = (eventId) => {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvent = async () => {
      if (!eventId) {
        setError('Event ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const eventData = await eventRepository.getEventById(eventId);
        setEvent(eventData);
      } catch (err) {
        console.error('Error fetching event:', err);
        setError(err.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventId]);

  return { event, loading, error };
};

/**
 * Custom hook to fetch latest events
 * @param {number} limit - Number of events to fetch
 * @returns {Object} Object containing events, loading state, and error
 */
export const useLatestEvents = (limit = 4) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const eventsData = await eventRepository.getLatestEvents(limit);
        setEvents(eventsData);
      } catch (err) {
        console.error('Error fetching latest events:', err);
        setError(err.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [limit]);

  return { events, loading, error };
};

/**
 * Custom hook to fetch upcoming events
 * @param {number} limit - Number of events to fetch
 * @returns {Object} Object containing events, loading state, and error
 */
export const useUpcomingEvents = (limit = 10) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const eventsData = await eventRepository.getUpcomingEvents(limit);
        setEvents(eventsData);
      } catch (err) {
        console.error('Error fetching upcoming events:', err);
        setError(err.message || 'Failed to load upcoming events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [limit]);

  return { events, loading, error };
};

/**
 * Custom hook to fetch past events
 * @param {number} limit - Number of events to fetch
 * @returns {Object} Object containing events, loading state, and error
 */
export const usePastEvents = (limit = 10) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const eventsData = await eventRepository.getPastEvents(limit);
        setEvents(eventsData);
      } catch (err) {
        console.error('Error fetching past events:', err);
        setError(err.message || 'Failed to load past events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [limit]);

  return { events, loading, error };
};

/**
 * Custom hook to fetch paginated events
 * @param {number} page - Page number (0-indexed)
 * @returns {Object} Object containing events, pagination info, loading state, and error
 */
export const usePaginatedEvents = (page = 0) => {
  const [data, setData] = useState({
    events: [],
    pagination: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const [events, pagination] = await Promise.all([
          eventRepository.getEventsForPage(page),
          eventRepository.getPaginationInfo(page)
        ]);

        setData({ events, pagination });
      } catch (err) {
        console.error('Error fetching paginated events:', err);
        setError(err.message || 'Failed to load events');
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [page]);

  return { ...data, loading, error };
};

/**
 * Custom hook to search events
 * @param {string} searchTerm - Search term
 * @param {number} debounceMs - Debounce delay in milliseconds
 * @returns {Object} Object containing events, loading state, and error
 */
export const useEventSearch = (searchTerm, debounceMs = 300) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!searchTerm || searchTerm.trim() === '') {
      setEvents([]);
      setLoading(false);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        setError(null);
        
        const results = await eventRepository.searchEvents(searchTerm);
        setEvents(results);
      } catch (err) {
        console.error('Error searching events:', err);
        setError(err.message || 'Failed to search events');
      } finally {
        setLoading(false);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchTerm, debounceMs]);

  return { events, loading, error };
};
